<template>
  <div class="column content-box settings--content">
    <p class="knowledge-base-alert">
      {{ $t('INTEGRATION_SETTINGS.ASSISTENT.PRIVATE_KNOWLEDGE_BASE.ALERT') }}
    </p>
    <label>
      {{ $t('INTEGRATION_SETTINGS.ASSISTENT.FORM.FILES.LABEL') }}
    </label>
    <div v-if="hasAttachments">
      <attachment-preview
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>
    <div v-if="isUploadingFile" class="file-upload-loading">
      <spinner :color-scheme="'dark'" />
      <span>{{
        $t('INTEGRATION_SETTINGS.ASSISTENT.FORM.FILES.SUBMITTING')
      }}</span>
    </div>
    <file-upload
      v-else
      ref="files"
      name="_file"
      :accept="acceptedFormats.join(',')"
      :size="acceptedSize"
      :data="{ direct_upload: false }"
      @input-file="onFileUpload"
    >
      <woot-button
        class-names="button--upload"
        icon="file-upload"
        color-scheme="secondary"
        variant="smooth"
        size="small"
      >
        <span>{{
          $t('INTEGRATION_SETTINGS.ASSISTENT.FORM.FILES.BUTTON_ADD_TEXT')
        }}</span>
      </woot-button>

      <small>
        {{
          $t(
            'INTEGRATION_SETTINGS.ASSISTENT.FORM.HELP_TEXT_ACCEPTED_FILE_TYPES'
          )
        }}: <span>{{ acceptedFormats.join(', ') }}</span>
      </small>
    </file-upload>

    <div class="tab-footer">
      <woot-submit-button
        :loading="isSaving"
        :disabled="isUploadingFile"
        :button-text="$t('INTEGRATION_SETTINGS.ASSISTENT.UPDATE.BUTTON_TEXT')"
        @click="onSubmit"
      />
    </div>
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component';
import assistentTabMixin from '../../mixins/assistentTabMixin';
import {
  checkFileSizeLimit,
  checkAcceptedFile,
} from 'shared/helpers/FileHelper';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import Spinner from 'shared/components/Spinner';
import assistentAPI from 'dashboard/api/assistent';

const convertFileToResource = files => {
  return files.map(file => ({
    resource: file,
    isPrivate: false,
    thumb: file.url,
    blobSignedId: file.blob_id,
    openaiFileId: file?.openai_file_id,
  }));
};

export default {
  components: {
    FileUpload,
    AttachmentPreview,
    Spinner,
  },
  mixins: [assistentTabMixin],
  data() {
    return {
      attachedFiles: this.assistent.files
        ? convertFileToResource(this.assistent.files)
        : [],
      vectorStoreId: this.assistent.vector_store_id || null,
      filesTemp: [],
      acceptedSize: 200 * 1024 * 1024, // 200MB
      acceptedFormats: ['.pdf', '.json', '.txt', '.doc', '.docx'],
      isUploadingFile: false,
    };
  },
  computed: {
    hasAttachments() {
      return this.attachedFiles.length;
    },
  },
  beforeDestroy() {
    this.$store.dispatch('assistent/submitDeleteTempFilesAndVectorStore');
  },
  methods: {
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    async onFileUpload(file) {
      if (!file) return;
      const MAXIMUM_SIZE = this.acceptedSize;
      const validFileSizeLimit = checkFileSizeLimit(file, MAXIMUM_SIZE);
      const validFileType = checkAcceptedFile(file, this.acceptedFormats);
      if (validFileSizeLimit && validFileType) {
        this.isUploadingFile = true;
        try {
          const response = await assistentAPI.uploadFileToOpenAI(
            file.file,
            this.assistent.name,
            this.vectorStoreId
          );
          const {
            openai_file_id: openaiFileId,
            vector_store_id: vectorStoreId,
            blob_signed_id: blobSignedId,
            filename,
          } = response.data;

          this.vectorStoreId = vectorStoreId;

          const reader = new FileReader();
          reader.readAsDataURL(file.file);
          reader.onloadend = () => {
            this.attachedFiles.push({
              resource: file,
              isPrivate: false,
              thumb: reader.result,
              blobSignedId,
              path: `${blobSignedId}/${filename}`,
              openaiFileId,
            });

            this.filesTemp.push({ blobSignedId, openaiFileId });
            this.$store.dispatch(
              'assistent/setTempFiles',
              this.filesTemp.map(f => f.blobSignedId)
            );
            this.isUploadingFile = false;
          };
        } catch (uploadError) {
          this.isUploadingFile = false;
          this.showAlert(
            this.$t('INTEGRATION_SETTINGS.ASSISTENT.FORM.FILES.ERROR')
          );
        }
      } else if (!validFileSizeLimit) {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', { MAXIMUM_SIZE })
        );
      } else if (!validFileType) {
        this.showAlert(this.$t('CONVERSATION.FILE_TYPE_NOT_ACCEPTED'));
      }
    },
    async onSubmit() {
      await this.saveAssistent({
        vector_store_id: this.vectorStoreId,
        file_ids: this.attachedFiles.map(f => f.openaiFileId),
        files: this.attachedFiles.map(f => f.blobSignedId),
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import './tabs-shared.scss';
</style>
